import type { Control, UseFormSetValue } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../common/components/Input";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-quizz-3 stores three PARALLEL, index-aligned fields:
//   list_answers     string[]   — answer text   (per-language, translatable PLD)
//   correct_answers  boolean[]  — correctness    (base content only, shared)
//   has_multiple_answers boolean — single vs multi-correct (base content only)
// This bespoke editor keeps the paired view but writes each array straight
// back to react-hook-form so the existing save path round-trips the exact
// shape Spatial Studio writes. See docs/plans/todo/node-form-quizz-json-editors.md §3.1.

// minItems from the slopify jsonSchema; soft max from the documented generation
// range ("Emit 3-4 options"). Existing data with more rows is preserved — we
// only stop the user from adding past the cap, never force removal.
const MIN_ANSWERS = 2;
const MAX_ANSWERS = 4;

interface QuizThreeAnswersFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (add/remove/correctness/has_multiple) are source-language only. */
  isSourceLanguage: boolean;
  /** Optional per-answer character cap (none in the current schema). */
  maxLength?: number;
}

const QuizThreeAnswersField = ({
  control,
  setValue,
  label,
  disabled = false,
  isSourceLanguage,
  maxLength,
}: QuizThreeAnswersFieldProps) => {
  const { t } = useTranslation();

  const watchedList = useWatch({ control, name: "list_answers" });
  const watchedCorrect = useWatch({ control, name: "correct_answers" });
  const watchedHasMultiple = useWatch({ control, name: "has_multiple_answers" });

  const listAnswers: string[] = Array.isArray(watchedList) ? watchedList : [];
  const correctAnswers: boolean[] = Array.isArray(watchedCorrect) ? watchedCorrect : [];
  const hasMultiple = !!watchedHasMultiple;

  // Text is editable in any language; structure is locked outside the source.
  const structureLocked = disabled || !isSourceLanguage;

  const writeList = (next: string[]) => setValue("list_answers", next, { shouldDirty: true });
  const writeCorrect = (next: boolean[]) =>
    setValue("correct_answers", next, { shouldDirty: true });

  const handleTextChange = (index: number, value: string) => {
    const next = [...listAnswers];
    next[index] = value;
    writeList(next);
  };

  const handleToggleCorrect = (index: number) => {
    if (hasMultiple) {
      // Free multi-select: flip just this row, keep its array length aligned.
      const next = listAnswers.map((_, i) =>
        i === index ? !correctAnswers[i] : !!correctAnswers[i],
      );
      writeCorrect(next);
    } else {
      // Single-answer: exactly this row true, all others false.
      writeCorrect(listAnswers.map((_, i) => i === index));
    }
  };

  const handleAdd = () => {
    writeList([...listAnswers, ""]);
    writeCorrect([...listAnswers.map((_, i) => !!correctAnswers[i]), false]);
  };

  const handleRemove = (index: number) => {
    writeList(listAnswers.filter((_, i) => i !== index));
    writeCorrect(
      listAnswers
        .filter((_, i) => i !== index)
        .map((_, i) => {
          // re-read correctness skipping the removed index
          const sourceIndex = i < index ? i : i + 1;
          return !!correctAnswers[sourceIndex];
        }),
    );
  };

  const handleToggleHasMultiple = () => {
    const next = !hasMultiple;
    setValue("has_multiple_answers", next, { shouldDirty: true });
    if (!next) {
      // Switching to single-answer: coerce to exactly one correct. Keep the
      // first currently-correct row; if none are correct, leave all false so
      // the author makes an explicit choice.
      const firstCorrect = correctAnswers.findIndex(Boolean);
      if (firstCorrect >= 0) {
        writeCorrect(listAnswers.map((_, i) => i === firstCorrect));
      }
    }
  };

  const canAdd = !structureLocked && listAnswers.length < MAX_ANSWERS;
  const canRemove = !structureLocked && listAnswers.length > MIN_ANSWERS;

  // A quizz with no correct answer is unplayable. Surface it here and block the
  // save in NodeForm (see quizz3MissingCorrectAnswer). Only meaningful in the
  // source language, where correctness is editable.
  const hasNoCorrectAnswer = isSourceLanguage && !correctAnswers.some(Boolean);

  // Surface WHY the add/remove buttons disable at the min/max — a silently
  // greyed button reads as a bug. Grey info, shown only when no blocking error
  // (no-correct-answer) needs the line, and only where structure is editable.
  const atMinAnswers = listAnswers.length <= MIN_ANSWERS;
  const atMaxAnswers = listAnswers.length >= MAX_ANSWERS;
  const showMinAnswersHint = !disabled && isSourceLanguage && atMinAnswers && !hasNoCorrectAnswer;
  const showMaxAnswersHint =
    !disabled && isSourceLanguage && atMaxAnswers && !atMinAnswers && !hasNoCorrectAnswer;
  const minAnswersHint = t("pages.project.editNode.quizz3MinAnswersHint") as string;
  const maxAnswersHint = t("pages.project.editNode.quizz3MaxAnswersHint") as string;

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <Tooltip
          message={!canAdd && !structureLocked ? maxAnswersHint : ""}
          className="shrink-0"
          classNameChildren="right-0"
        >
          <button
            type="button"
            onClick={handleAdd}
            disabled={!canAdd}
            className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            <span>{t("pages.project.editNode.quizzAddAnswer")}</span>
          </button>
        </Tooltip>
      </div>

      <label className="flex items-center gap-2 mb-3 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={hasMultiple}
          onChange={handleToggleHasMultiple}
          disabled={structureLocked}
          className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
        />
        {t("pages.project.editNode.quizzHasMultipleAnswers")}
      </label>

      {!isSourceLanguage && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      )}

      {/* Single reserved hint line (avoids layout shift). Priority: the blocking
          no-correct-answer error (red), then the informational min/max-answers
          note (grey) explaining why the remove / add buttons are disabled. */}
      <p
        className={`min-h-[1.25rem] mb-2 text-xs font-medium ${
          showMinAnswersHint || showMaxAnswersHint ? "text-gray-500 italic" : "text-red-600"
        }`}
      >
        {hasNoCorrectAnswer && !disabled
          ? t("pages.project.editNode.quizzNeedsCorrectAnswer")
          : showMinAnswersHint
          ? minAnswersHint
          : showMaxAnswersHint
          ? maxAnswersHint
          : ""}
      </p>

      <div className="space-y-2">
        {listAnswers.map((answerText, index) => {
          const isCorrect = !!correctAnswers[index];
          return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-3 flex items-center gap-3"
            >
              <label
                className="flex items-center gap-2 shrink-0"
                title={t("pages.project.editNode.quizzMarkCorrect")}
              >
                <input
                  type={hasMultiple ? "checkbox" : "radio"}
                  name="quizz3-correct"
                  checked={isCorrect}
                  onChange={() => handleToggleCorrect(index)}
                  disabled={structureLocked}
                  className={structureLocked ? "cursor-not-allowed" : "cursor-pointer"}
                />
                <span className="text-xs text-gray-500">
                  {t("pages.project.editNode.quizzMarkCorrect")}
                </span>
              </label>

              <div className="flex-1 min-w-0">
                <Input
                  type="text"
                  value={answerText ?? ""}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  placeholder={t("pages.project.editNode.quizzAnswerPlaceholder") as string}
                  className="dynamic-field"
                />
              </div>

              <Tooltip
                message={!canRemove && !structureLocked ? minAnswersHint : ""}
                className="shrink-0"
                classNameChildren="right-0"
              >
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  disabled={!canRemove}
                  className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={t("general.delete") as string}
                >
                  <MdDeleteOutline size={20} />
                </button>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizThreeAnswersField;
